import { useAppDispatch } from '@fd/customHooks/useAppDispatch';
import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import { BookOpen02, GraduationHat01, HelpCircle, MessageChatSquare } from '@flipdish/ui-icons';
import { getActiveLanguage, getTranslate } from 'react-localize-redux';
import { add, remove } from 'react-redux-permissions';
import { useLocation } from 'react-router-dom';

import { accountActions } from '../../../../actions/account.actions';
import { useHasAccessToOnboardingV3 } from '../../../../components/OnboardingV3/hooks/useHasAccessToOnboardingV3';
import { getSupportLanguageUrl } from '../../../../helpers/supportLinks';
import { isProductBasedMenusEnabled } from '../../../../selectors/app.selector';
import { selectPortalUser } from '../../../../viewConfiguration';
import { type ItemMetaData, useMenuItemsConfig } from '../menuItemsConfig';
import { type SidebarNavItem, type TransformOptions, transformItems } from '../utils/sidebar.utils';
import { useSidebarAccountMenu } from './useSidebarAccountMenu';

export const useSidebarNavItems = () => {
  const dispatch = useAppDispatch();
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const activeLanguage = useAppSelector((state) => getActiveLanguage(state.locale));
  const currentApp = useAppSelector((state) => state.currentApp);
  const account = useAppSelector((state) => state.account);
  const portalUser = useAppSelector(selectPortalUser);
  const isProductBasedMenusOn = useAppSelector((state) => isProductBasedMenusEnabled(state));
  const { data: rmsData } = useQueryRMSCommonDataHook(currentApp?.AppId || '');
  const location = useLocation();

  const appId = currentApp?.AppId ?? 'global';
  const { reducedOnboardingView, onboardingWaitingView } = useHasAccessToOnboardingV3();
  const isRestrictedOnboardingNav = reducedOnboardingView || onboardingWaitingView;
  const showAppScopedLinks =
    !isRestrictedOnboardingNav && appId !== 'global' && appId !== 'flipdish-global';

  const rawItems = useMenuItemsConfig(
    currentApp,
    isProductBasedMenusOn,
    rmsData?.associations?.menuSource,
    rmsData?.subscriptions
  );
  const accountData = useSidebarAccountMenu({
    appId,
    showAppScopedLinks,
    isRestrictedOnboardingNav,
  });

  const handleHiddenFeatureChange = (isSelected: boolean) => {
    void dispatch(accountActions.update({ ...account, ShowHiddenFeatures: isSelected }))
      .then(() => {
        dispatch(isSelected ? add('ShowHiddenFeatures') : remove('ShowHiddenFeatures'));
      })
      .catch(() => {
        // accountActions.update already dispatches updateFailure.
      });
  };

  const getToggle = (item: ItemMetaData) => {
    if (item.toggle !== 'showHiddenFeatures') return undefined;
    return {
      label: translate('Show_hidden_features') as string,
      isSelected: !!account.ShowHiddenFeatures,
      onChange: handleHiddenFeatureChange,
    };
  };

  const mfeHrefs = new Set<string>();
  const collectMfe: TransformOptions = { onMfeHref: (href) => mfeHrefs.add(href) };

  const items = transformItems(
    rawItems.filter((item) => item.section !== 'footer'),
    portalUser,
    translate,
    undefined,
    collectMfe
  );
  const configFooterItems = transformItems(
    rawItems.filter((item) => item.section === 'footer'),
    portalUser,
    translate,
    getToggle,
    collectMfe
  );

  const supportItem: SidebarNavItem = {
    label: translate('Support') as string,
    icon: HelpCircle,
    items: [
      {
        label: translate('Support_articles') as string,
        href: getSupportLanguageUrl(activeLanguage),
        icon: BookOpen02,
      },
      {
        label: translate('Talk_to_us') as string,
        href: 'https://help.flipdish.com/s/contactsupport?language=en_GB',
        icon: MessageChatSquare,
      },
      {
        label: translate('Flipdish_Academy') as string,
        href: 'https://academy.flipdish.com/',
        icon: GraduationHat01,
      },
    ],
  };

  const footerItems: SidebarNavItem[] = [
    ...(!isRestrictedOnboardingNav ? [supportItem] : []),
    ...configFooterItems,
  ];

  return {
    items,
    footerItems,
    account: accountData,
    activeUrl: location.pathname,
    // MFEs only re-read the URL on popstate, which history.push never fires. Wait a frame so the push has landed.
    onNavigate: (href: string) => {
      if (!mfeHrefs.has(href)) return;
      requestAnimationFrame(() => window.dispatchEvent(new PopStateEvent('popstate')));
    },
  };
};
